import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 40;

/**
 * A custom element that manages a header menu.
 *
 * The submenu open/close visual state is driven entirely by CSS (`:hover`,
 * `:focus-within`, and `[aria-expanded='true']`). This component only keeps the
 * `aria-expanded` state in sync for accessibility, dispatches the
 * {@link MegaMenuHoverEvent} consumed by the chat drawer, primes the Products
 * mega menu loading state, eagerly loads submenu imagery, and handles Escape.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item link.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = [];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeydown);
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the hovered/focused menu item.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);
    if (!submenu) return;

    // Announce the open mega menu so the chat drawer can step aside. Pointer
    // interactions wait a beat so a quick skim across items doesn't thrash it.
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    if (event instanceof FocusEvent) {
      this.dispatchEvent(new MegaMenuHoverEvent());
    } else {
      const committedItem = item;
      this.#hoverDispatchTimer = setTimeout(() => {
        this.#hoverDispatchTimer = undefined;
        if (this.#state.activeItem === committedItem) {
          this.dispatchEvent(new MegaMenuHoverEvent());
        }
      }, HOVER_COMMIT_DELAY_MS);
    }

    this.#triggerProductsLoading(item, submenu);
  };

  /**
   * Deactivate the active item unless focus is moving into its submenu.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const submenu = findSubmenu(this.#state.activeItem);
    const movingIntoSubmenu =
      event.relatedTarget instanceof Node && submenu instanceof Node && submenu.contains(event.relatedTarget);

    if (movingIntoSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately.
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
  };

  /**
   * Close the open submenu when Escape is pressed and return focus to the trigger.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    const activeItem = this.#state.activeItem;
    this.#deactivate();
    activeItem.focus();
  };

  /**
   * Give the Products mega menu a brief loading state while its images are
   * decoded. A short minimum duration preserves feedback for cached images.
   * @param {HTMLElement} item
   * @param {HTMLElement} submenu
   */
  #triggerProductsLoading(item, submenu) {
    if (!submenu.querySelector('.mega-menu--products-only')) return;

    const images = Array.from(submenu.querySelectorAll('img'));
    if (submenu.querySelector('.resource-card, .nova-menu-product-card') && images.every((image) => image.complete)) return;

    const requestId = String(Date.now());
    const startedAt = performance.now();
    item.dataset.productsLoading = requestId;

    const finish = () => {
      const remaining = Math.max(0, 220 - (performance.now() - startedAt));
      window.setTimeout(() => {
        if (item.dataset.productsLoading === requestId) {
          delete item.dataset.productsLoading;
        }
      }, remaining);
    };

    if (!images.length) {
      finish();
      return;
    }

    const waitForImageDecode = (image) => {
      const decode = () => (typeof image.decode === 'function' ? image.decode().catch(() => undefined) : Promise.resolve());

      if (image.complete) return decode();

      return new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      }).then(decode);
    };

    Promise.all(images.map(waitForImageDecode)).then(finish);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the menu item link within (or matching) the given element.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const menuitem = element.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the submenu belonging to the given menu item link.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
